import EnrolmentsGrid from "../EnrolmentsGrid";
import React, {useEffect} from "react";
import deltas from "../../../redux/actions/deltas";
import {connect} from "react-redux";
import CheckboxField from "../../atoms/CheckboxField";
import SelectInput from "../../atoms/SelectInput";
import signals from "../../../redux/actions/signals";
import roles from "../../../redux/roles";
import {createSelector} from 'reselect';
import EnrolmentsAndCohortsPollingAlertMessage from "../../molecules/EnrolmentsAndCohortsPollingAlertMessage";
import {
    GRID_FILTER_TYPE_DATE,
    GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD,
    GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC,
    GRID_FILTER_TYPE_BOOLEAN,
} from "../../molecules/GridFilter";
import {OffsetPagination} from '@zendeskgarden/react-pagination';
import {ThemeProvider} from '@zendeskgarden/react-theming';
import paginationLabels from "../../molecules/paginationLabels";
import DownloadButton from "../../molecules/DownloadButton";
import {selectIsLoadingDownloadEnrolmentsCsv} from "../../../redux/reducers/states/downloadEnrolmentsCsv";

export const allColumns = [
    { accessor: 'id', Header: 'Id', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC },
    { accessor: 'fullName', Header: 'Full Name', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC },
    { accessor: 'email', Header: 'Email', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC },
    { accessor: 'phone', Header: 'Phone', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC },
    { accessor: 'town', Header: 'Town', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC },
    { accessor: 'schoolName', Header: 'School', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC },
    { accessor: 'cohortName', Header: 'Cohort', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    { accessor: 'courseName', Header: 'Course', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    { accessor: 'providerName', Header: 'Provider', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    { accessor: 'regionName', Header: 'Course Region', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    { accessor: 'deliveryTypeName', Header: 'Training Delivery Type', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD},
    { accessor: 'signedUpAt', Header: 'Sign Up Date', gridFilterType: GRID_FILTER_TYPE_DATE },
    { accessor: 'isContractIssued', Header: 'Contract Issued', gridFilterType: GRID_FILTER_TYPE_BOOLEAN },
    { accessor: 'isContractSigned', Header: 'Contract Signed', gridFilterType: GRID_FILTER_TYPE_BOOLEAN },
    { accessor: 'contractSignedAt', Header: 'Contract Signed At', gridFilterType: GRID_FILTER_TYPE_DATE },
    { accessor: 'completedAt', Header: 'Completed At', gridFilterType: GRID_FILTER_TYPE_DATE },
    { accessor: 'status', Header: 'Status', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
];

const columnsProviderDoesNotSeeByKey = [
    'id',
    'providerName',
];

export const cecColumns = allColumns;
export const providerColumns = allColumns.filter(column => !columnsProviderDoesNotSeeByKey.includes(column.accessor));

const {
    hideEnrolmentsGridColumn,
    showEnrolmentsGridColumn,
    showAllEnrolmentsGridColumns,
    resetEnrolmentsGridFilterAndSort,
    clearEnrolmentsGridRowsSelected,
    setEnrolmentsGridAllVisibleRowsSelected,
    setEnrolmentsGridCurrentPage,
} = deltas.actionCreators;

const {
    downloadEnrolmentsCsv,
    reloadEnrolmentsAndCohorts,
} = signals.actionCreators;

const getAreAllVisibleRowsSelected = createSelector(
    state => state.enrolmentsGrid.filteredSortedData,
    state => state.enrolmentsGrid.selectedRows,
    (filteredSortedData, selectedRows) => filteredSortedData.map(row => row.id).every(id => selectedRows.includes(id)));

const getBaseColumns = createSelector(
    state => state.role,
    role => (roles.CEC === role) ? cecColumns : providerColumns,
);

const getColumnsToShow = createSelector(
    state => state.enrolmentsGrid.columnsToHide,
    getBaseColumns,
    (columnsToHide, baseColumns) => baseColumns.filter(column => !columnsToHide.includes(column.id || column.accessor)),
);

const mapStateToProps = state => ({
    isCec: roles.CEC === state.role,
    columnsToHide: state.enrolmentsGrid.columnsToHide,
    baseColumns: getBaseColumns(state),
    columnsToShow: getColumnsToShow(state),
    allVisibleRowsSelected: getAreAllVisibleRowsSelected(state),
    currentPage: state.enrolmentsGrid.currentPage,
    enrolmentsPaginationData: state.data.enrolmentsPaginationData,
    isLoadingDownloadEnrolmentsCsv: selectIsLoadingDownloadEnrolmentsCsv(state),
});

const mapDispatchToProps = (dispatch) => ({
    hideEnrolmentsGridColumn: column => dispatch(hideEnrolmentsGridColumn(column)),
    showEnrolmentsGridColumn: column => dispatch(showEnrolmentsGridColumn(column)),
    showAllEnrolmentsGridColumns: () => dispatch(showAllEnrolmentsGridColumns()),
    resetEnrolmentsGridFilterAndSort: () => dispatch(resetEnrolmentsGridFilterAndSort()),
    downloadEnrolmentsCsv: () => dispatch(downloadEnrolmentsCsv()),
    reloadEnrolmentsAndCohorts: page => dispatch(reloadEnrolmentsAndCohorts(page)),
    clearEnrolmentsGridRowsSelected: () => dispatch(clearEnrolmentsGridRowsSelected()),
    setEnrolmentsGridAllVisibleRowsSelected: () => dispatch(setEnrolmentsGridAllVisibleRowsSelected()),
    setEnrolmentsGridCurrentPage: page => dispatch(setEnrolmentsGridCurrentPage(page)),
});

const EnrolmentsSection = ({resetEnrolmentsGridFilterAndSort, hideEnrolmentsGridColumn, showEnrolmentsGridColumn, showAllEnrolmentsGridColumns, downloadEnrolmentsCsv, baseColumns, columnsToHide, columnsToShow, clearEnrolmentsGridRowsSelected, setEnrolmentsGridAllVisibleRowsSelected, allVisibleRowsSelected, reloadEnrolmentsAndCohorts, setEnrolmentsGridCurrentPage, enrolmentsPaginationData, currentPage, isLoadingDownloadEnrolmentsCsv}) => {

    const formatOptionLabel = ({ value, label }) => (
        <div>
            <CheckboxField
                label={label}
                checked={!columnsToHide.includes(value) && 'ALL' !== value }
            />
        </div>
    );

    return <React.Fragment>
        <EnrolmentsAndCohortsPollingAlertMessage />
        <div className="d-block d-md-flex mb-3 mt-4">
            <div className="col-md-3 position-relative px-0 no-padding me-2">
                <SelectInput
                    id="o-columns"
                    placeholder="Hide and show columns"
                    isMulti={true}
                    overrideOnChange={(selectedOptions, {option: {value}}) => {
                        if (value === 'ALL') {
                            showAllEnrolmentsGridColumns();
                            return;
                        }

                        !columnsToHide.includes(value)
                            ? hideEnrolmentsGridColumn(value)
                            : showEnrolmentsGridColumn(value);
                    }}
                    options={
                        (
                            0 === columnsToHide.length
                            ? []
                            : [{label: 'Show All', value: 'ALL'}]
                        ).concat(
                            baseColumns.map(({accessor, Header}) => ({label: Header, value: accessor}))
                        )
                    }
                    {...{formatOptionLabel}}
                    styles={
                        {
                            container: (provided) => ({
                                ...provided,
                                height: '100%',
                            }),
                            control: (provided) => ({
                                ...provided,
                                height: '100%',
                            })
                        }
                    }
                />
            </div>
            <div className="text-justify mt-3 mt-md-auto ms-md-auto mb-n2">
                {/*Not needed as bulk actions not currently available - CLTPS-96*/}
                {/*<button className="cec-btn cec-btn-secondary-teal me-2 mb-2" data-cy="select/unselect-all-button" onClick={*/}
                {/*    allVisibleRowsSelected ? clearEnrolmentsGridRowsSelected : setEnrolmentsGridAllVisibleRowsSelected*/}
                {/*}>*/}
                {/*    {allVisibleRowsSelected ? 'Unselect all' : 'Select all'}*/}
                {/*</button>*/}
                <button className="cec-btn cec-btn-secondary-teal me-2 mb-2" data-cy="enrolments--reset-table-button"
                    onClick={() => {
                        resetEnrolmentsGridFilterAndSort();
                        reloadEnrolmentsAndCohorts();
                    }}
                >
                    Reset table
                </button>
                <DownloadButton
                    buttonText='Download CSV'
                    isLoading={isLoadingDownloadEnrolmentsCsv}
                    className='cec-btn cec-btn-primary-teal mb-2'
                    data-cy="download-csv-button"
                    onClick={downloadEnrolmentsCsv}
                />
            </div>
        </div>
        <EnrolmentsGrid columns={columnsToShow}/>
        <div className="d-block d-md-flex justify-content-end mb-3 mt-4">
            <ThemeProvider>
            <OffsetPagination
                id="cec-pagination"
                aria-label="Enrolments pagination"
                totalPages={enrolmentsPaginationData.totalPages}
                currentPage={currentPage}
                onChange={(currentPage) => {
                    setEnrolmentsGridCurrentPage(currentPage);
                    reloadEnrolmentsAndCohorts();
                }}
                labels={paginationLabels}
            />
            </ThemeProvider>
        </div>
    </React.Fragment>;
};

export default connect(mapStateToProps, mapDispatchToProps)(EnrolmentsSection);
